import { cloneElement, isValidElement, useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/shared/lib/utils";
import { useFloatingPosition, type Placement } from "./floating";

type TooltipProps = {
  /** Conteúdo do tooltip. Vazio/null → só renderiza o filho. */
  content: ReactNode;
  children: ReactElement;
  side?: Placement;
  /** Atraso ao passar o mouse (foco por teclado mostra na hora). */
  delay?: number;
  className?: string;
  /** Desliga sem desmontar (ex.: sidebar expandida não precisa de tooltip). */
  disabled?: boolean;
};

/**
 * Tooltip acessível: abre em hover (com atraso) e em foco por teclado, fecha com Esc.
 * O filho recebe `aria-describedby`. Para botões só-ícone use `IconButton` (já traz tooltip + aria-label).
 */
export function Tooltip({ content, children, side = "top", delay = 350, className, disabled }: TooltipProps) {
  const id = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const pos = useFloatingPosition(open, anchorRef, tipRef, side);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  if (disabled || content == null || content === "") return children;

  const show = (immediate: boolean) => {
    if (timer.current) window.clearTimeout(timer.current);
    if (immediate) setOpen(true);
    else timer.current = window.setTimeout(() => setOpen(true), delay);
  };
  const hide = () => {
    if (timer.current) window.clearTimeout(timer.current);
    setOpen(false);
  };

  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<{ "aria-describedby"?: string }>, { "aria-describedby": open ? id : undefined })
    : children;

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex max-w-full"
        onMouseEnter={() => show(false)}
        onMouseLeave={hide}
        onFocus={(e) => show(e.target.matches(":focus-visible"))}
        onBlur={hide}
        onPointerDown={hide}
      >
        {child}
      </span>
      {open
        ? createPortal(
            <div
              ref={tipRef}
              id={id}
              role="tooltip"
              style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
              className={cn(
                "pointer-events-none z-[300] max-w-xs rounded-md bg-fg px-2 py-1 text-caption font-medium text-fg-inverse shadow-md",
                pos ? "animate-fade-in" : "opacity-0",
                className,
              )}
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
